/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React, { useState } from "react";
import {
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import { useAppDownloadContext } from "../../../../Basis/Components/AppDownloadContext";
import AppLink from "../../../../Basis/Components/AppLink";
import InnerView from "../../../../Basis/Components/InnerView";
import MaxWidthTableCell from "../../../../Basis/Components/MaxWidthTableCell";
import ViewHeader from "../../../../Basis/Components/ViewHeader";
import ViewMainContent from "../../../../Basis/Components/ViewMainContent";
import { DownloadIcon } from "../../../../Basis/FontAwesome";
import StripeClient from "../../Api/StripeClient";
import { belegartTranslationKeysMap, belegstatusTranslationKeysMap, translationKeys, useTranslation } from "../../Internationalisierung/i18nMandanten";
import Beleg from "../../Queries/Beleg";
import Belegart from "../../Queries/Belegart";
import LadeBelegeZuMandant from "../../Queries/LadeBelegeZuMandant";
import BelegeUrlsBuilder from "../../Utils/BelegeUrlsBuilder";
import MandantBelegUtil from "../../Utils/MandantBelegUtil";

export default function MandantBelegeView() {
    const [belege, setBelege] = useState<Beleg[] | undefined>(undefined);
    const { t, tValue } = useTranslation();
    const appDownloadContext = useAppDownloadContext();
    const stripeClient = new StripeClient();
    new LadeBelegeZuMandant((belege) => setBelege(belege));

    async function downloadPdf(belegart: Belegart, belegId: string) {
        await MandantBelegUtil.downloadPdf(
            stripeClient,
            appDownloadContext,
            belegart,
            belegId)
    }

    return (
        <InnerView>
            <ViewHeader title={t(translationKeys.mandantBelegeView.belege)} />
            <ViewMainContent loading={!belege}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {t(translationKeys.mandantBelegeView.beleg)}
                                </TableCell>
                                <TableCell>
                                    {t(translationKeys.mandantBelegeView.datum)}
                                </TableCell>
                                <TableCell>
                                    {t(translationKeys.mandantBelegeView.art)}
                                </TableCell>
                                <TableCell>
                                    {t(translationKeys.mandantBelegeView.betrag)}
                                </TableCell>
                                <TableCell>
                                    {t(translationKeys.mandantBelegeView.status)}
                                </TableCell>
                                <TableCell align="center">
                                    {t(translationKeys.mandantBelegeView.download)}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {belege?.map(beleg => (
                                <TableRow hover key={beleg.id}>
                                    <MaxWidthTableCell>
                                        <AppLink to={BelegeUrlsBuilder.getBelegUrl(beleg.art, beleg.id)}>
                                            {beleg.nummer}
                                        </AppLink>
                                    </MaxWidthTableCell>
                                    <TableCell>{tValue(beleg.datum, "DateTime")}</TableCell>
                                    <TableCell>{t(belegartTranslationKeysMap[beleg.art])}</TableCell>
                                    <TableCell>{tValue(beleg.betrag, "Preis")}</TableCell>
                                    <TableCell>{t(belegstatusTranslationKeysMap[beleg.status])}</TableCell>
                                    <TableCell align="center">
                                        <IconButton onClick={() => downloadPdf(beleg.art, beleg.id)} size="large">
                                            <DownloadIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </ViewMainContent>
        </InnerView>
    );
}